import {
  Auth0ContextInterface,
  User,
  withAuth0,
  WithAuth0Props,
  withAuthenticationRequired,
} from '@auth0/auth0-react'
import { MainApp } from './components/main-app/main-app'
import { ErrorBoundary } from 'react-error-boundary'
import { useMatches, useNavigate } from 'react-router-dom'
import { useEffect, useMemo } from 'react'
import { getNavMenu, getRouteConfigForUser } from '@Components/main-app/route-mapping'
import { getAuthConfig } from './config'
import { RootState } from '@AppState/redux-store'
import { useSelector } from 'react-redux'
import { Button, Container, Flex, IconButton, Section, Text, Theme } from '@radix-ui/themes'
import { CustomerSwitcher } from '@Components/main-app/customer-switcher'
import { ExitIcon } from '@radix-ui/react-icons'
import { getLogoutParams } from '@Components/util/utils'
import GambytLogo from '@Assets/logos/gambyt-logo.svg'
import FreshworksWidgetLoader from '@Components/common/freshdesk-widget'
import { NewVersionBanner } from '@Components/main-app/new-version-banner'

const AppEntry = withAuthenticationRequired(({ auth0 }: { auth0: Auth0ContextInterface<User> }) => {
  const navigate = useNavigate()
  const { activeCustomerId, activeCustomerMetadata, currentUser } = useSelector(
    (state: RootState) => state.users,
  )
  const routeMatches = useMatches()

  const parentRoute = routeMatches[1] ? routeMatches[1].pathname : ''

  const routesForUser = useMemo(() => {
    return getRouteConfigForUser(currentUser)
  }, [currentUser])

  const navMenu = useMemo(() => {
    return getNavMenu(routeMatches, routesForUser, navigate)
  }, [routeMatches, routesForUser, navigate])

  useEffect(() => {
    // Ignore redirecting while auth is loading
    const routesLoading = routesForUser.length === 1 && routesForUser[0].routerConfig.path === '/'

    // If the user is landing on the root page, redirect them to the first route the have permissions for
    if (
      routesForUser.length &&
      routesForUser[0].routerConfig.path &&
      (!parentRoute || parentRoute === '/') &&
      !routesLoading
    ) {
      navigate(routesForUser[0].routerConfig.path, { replace: true })
    }
  }, [navigate, parentRoute, routesForUser])

  return (
    <ErrorBoundary fallback={<div>Uh oh something went wrong</div>}>
      {activeCustomerId && activeCustomerMetadata?.freshdeskId ? (
        <FreshworksWidgetLoader widgetId={activeCustomerMetadata?.freshdeskId} />
      ) : null}
      <Flex direction="column">
        <Theme appearance="dark">
          <Flex justify="center">
            <div className="header">
              <img src={GambytLogo} alt="gambyt logo" />
              <Flex className="tab-nav__container">{navMenu}</Flex>
              <Flex gap="9">
                <CustomerSwitcher />

                <IconButton
                  variant="ghost"
                  aria-label="Logout"
                  style={{ cursor: 'pointer', alignSelf: 'center' }}
                  onClick={() => auth0.logout(getLogoutParams())}>
                  <ExitIcon className="logout" height="20" width="20" />
                </IconButton>
              </Flex>
            </div>
          </Flex>
        </Theme>
        <NewVersionBanner />
        {import.meta.env.VITE_ENVIRONMENT && (
          <div className="environment-banner">
            {import.meta.env.VITE_ENVIRONMENT.toUpperCase()} ENVIRONMENT
          </div>
        )}
        <MainApp />
      </Flex>
    </ErrorBoundary>
  )
})

const isLoginRequiredError = (error: Error): boolean =>
  'error' in error && (error as { error: string }).error === 'login_required'

export const App = withAuth0((auth0Props: WithAuth0Props) => {
  const { error } = auth0Props.auth0

  useEffect(() => {
    // When the Auth0 session expires (e.g. after a long session or customer switch),
    // Auth0 redirects back with error=login_required. Instead of showing an error page,
    // automatically redirect to a full login and return the user to where they were.
    if (error && isLoginRequiredError(error)) {
      const organization = getAuthConfig().authorizationParams?.organization
      auth0Props.auth0.loginWithRedirect({
        authorizationParams: { organization },
        appState: { returnTo: window.location.pathname },
      })
    }
  }, [auth0Props.auth0, error])

  if (error) {
    if (isLoginRequiredError(error)) {
      return null
    }

    return (
      <Section>
        <Container>
          <Flex justify="center" direction="column" gap="3">
            <Text align="center">Error - {error.message || 'Unknown error'}</Text>
            <Flex justify="center">
              <Button
                style={{ cursor: 'pointer' }}
                onClick={() => auth0Props.auth0.logout(getLogoutParams())}>
                Logout
              </Button>
            </Flex>
          </Flex>
        </Container>
      </Section>
    )
  }

  return <AppEntry auth0={auth0Props.auth0} />
})
